import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  Signal,
  signal,
} from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { MENU_ITEMS } from './menu.const';
import { MenuComponent, SvgIconComponent } from '@ui-lib/components';
import { startWith } from 'rxjs';
import { LocalStorageService } from '../../../core/services/storage/local-storage.service';
import { TranslateModule } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserInformationService } from '../../../core/services/user/user-information.service';
import { PermissionService } from '../../../core/services/user/permission.service';
import { AccountComponent } from '../account/account.component';

const COLLAPSE_KEY = 'isMenuCollapsed';

@Component({
  standalone: true,
  selector: 'mon-layout',
  templateUrl: './layout.component.html',
  imports: [MenuComponent, RouterOutlet, TranslateModule, SvgIconComponent, AccountComponent],
  styleUrls: ['./layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent implements OnInit {
  private readonly userInformationService = inject(UserInformationService);

  private readonly userPermissions: Signal<string[]> = toSignal(
    this.userInformationService.userPermissions$.pipe(startWith<string[]>([])),
    { requireSync: true },
  );

  protected readonly menuItems = computed(() =>
    MENU_ITEMS.filter((item) => {
      const perm: string | undefined = item.requiredPermission;
      return (
        PermissionService.hasPermissionValue(this.userPermissions(), perm) &&
        PermissionService.hasAnyPermissionValue(this.userPermissions(), item.requiredPermissionsAny)
      );
    }),
  );

  private readonly localStorageService = inject(LocalStorageService);

  private readonly router = inject(Router);

  protected readonly isMenuCollapsed = signal<boolean>(false);

  protected navigateToMainPage(): void {
    this.router.navigate(['/']);
  }

  public ngOnInit(): void {
    const isMenuCollapsed = this.localStorageService.get(COLLAPSE_KEY);

    if (isMenuCollapsed) {
      try {
        this.isMenuCollapsed.set(JSON.parse(isMenuCollapsed));
      } catch (e) {
        console.error(e);
      }
    }
  }

  public toggleSidebar(): void {
    this.isMenuCollapsed.set(!this.isMenuCollapsed());
    this.localStorageService.set(COLLAPSE_KEY, this.isMenuCollapsed());
  }
}
